import { useState, useEffect, useCallback, useRef } from "react";

const PROXY    = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(PROXY + path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(getToken() ? { Authorization: "Bearer " + getToken() } : {}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const PLATFORMS = [
  { id: "shopify",  name: "Shopify",  color: "#5a8a00", bg: "#f3f9ec" },
  { id: "amazon",   name: "Amazon",   color: "#c45000", bg: "#fff3e8" },
  { id: "flipkart", name: "Flipkart", color: "#1b57c0", bg: "#ebf2ff" },
  { id: "myntra",   name: "Myntra",   color: "#b5003c", bg: "#fff0f4" },
  { id: "manual",   name: "Manual",   color: "#6b7280", bg: "#f3f4f6" },
];

const REASONS = [
  { id: "defective",           label: "Defective product"       },
  { id: "wrong_item",          label: "Wrong item sent"         },
  { id: "not_as_described",    label: "Not as described"        },
  { id: "damaged_in_transit",  label: "Damaged in transit"      },
  { id: "changed_mind",        label: "Customer changed mind"   },
  { id: "size_issue",          label: "Size / fit issue"        },
  { id: "other",               label: "Other"                   },
];

const STATUS_META = {
  requested:        { label: "Requested",        color: "#6b7280", bg: "#f3f4f6" },
  approved:         { label: "Approved",          color: "#2563eb", bg: "#eff6ff" },
  pickup_scheduled: { label: "Pickup Scheduled",  color: "#7c3aed", bg: "#f5f3ff" },
  in_transit:       { label: "In Transit",        color: "#d97706", bg: "#fffbeb" },
  received:         { label: "Received",          color: "#0891b2", bg: "#ecfeff" },
  inspected:        { label: "Inspected",         color: "#059669", bg: "#ecfdf5" },
  closed:           { label: "Closed",            color: "#15803d", bg: "#f0fdf4" },
  rejected:         { label: "Rejected",          color: "#dc2626", bg: "#fef2f2" },
};

const STATUS_FLOW = ["requested","approved","pickup_scheduled","in_transit","received","inspected","closed"];

const S = {
  card:    { background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", marginBottom: 14 },
  cardHdr: { padding: "11px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 600, fontSize: 13 },
  th:      { padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#6b7280", background: "#f9fafb", whiteSpace: "nowrap" },
  td:      { padding: "9px 12px", borderBottom: "1px solid #f3f4f6", verticalAlign: "middle", fontSize: 13 },
  btn:     (c="#F47920") => ({ padding: "6px 14px", borderRadius: 7, fontSize: 12, cursor: "pointer", background: c, color: "#fff", border: "none", fontFamily: "inherit", fontWeight: 600 }),
  btnOut:  { padding: "5px 10px", borderRadius: 7, fontSize: 11, cursor: "pointer", background: "#fff", color: "#374151", border: "1px solid #d1d5db", fontFamily: "inherit" },
  input:   { width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", outline: "none", marginBottom: 10 },
  label:   { fontSize: 11, color: "#6b7280", fontWeight: 600, display: "block", marginBottom: 3 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal:   { background: "#fff", borderRadius: 12, padding: 24, width: 520, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background: bg, color, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{label}</span>
);

const EMPTY_FORM = {
  platform: "shopify", returnType: "customer",
  orderNumber: "", customerName: "", customerEmail: "", customerPhone: "",
  productName: "", shopifySku: "", quantity: 1, salePrice: 0,
  reason: "defective", reasonNote: "", resolution: "pending", refundAmount: 0,
  awbNumber: "", courierName: "", notes: "",
};

export default function PageReturns({ toast, products = [], orders = [] }) {
  const [returns,     setReturns]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filterStatus,setFilterStatus]= useState("all");
  const [filterPlat,  setFilterPlat]  = useState("all");
  const [showModal,   setShowModal]   = useState(false);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [resolveModal,setResolveModal]= useState(null);
  const [resolveForm, setResolveForm] = useState({ resolution: "refund", refundAmount: 0 });
  const [pickupModal, setPickupModal] = useState(null);
  const [pickupForm,  setPickupForm]  = useState({ awbNumber: "", courierName: "", pickupDate: "" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api("/returns");
      setReturns(data.returns || []);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const createReturn = async () => {
    if (!form.productName) { toast("Enter product name", "error"); return; }
    if (!form.customerName && form.returnType === "customer") { toast("Enter customer name", "error"); return; }
    try {
      await api("/returns", { method: "POST", body: JSON.stringify(form) });
      toast("Return created ✓", "success");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch(e) { toast(e.message, "error"); }
  };

  const updateStatus = async (id, status, extra) => {
    try {
      await api("/returns/" + id + "/status", { method: "PUT", body: JSON.stringify({ status, ...extra }) });
      toast("Status updated ✓", "success");
      load();
    } catch(e) { toast(e.message, "error"); }
  };

  const restock = async (id) => {
    try {
      await api("/returns/" + id + "/restock", { method: "POST", body: JSON.stringify({ condition: "good" }) });
      toast("Restocked to inventory ✓", "success");
      load();
    } catch(e) { toast(e.message, "error"); }
  };

  const resolve = async () => {
    try {
      await api("/returns/" + resolveModal + "/resolve", { method: "PUT", body: JSON.stringify(resolveForm) });
      toast("Return resolved ✓", "success");
      setResolveModal(null);
      load();
    } catch(e) { toast(e.message, "error"); }
  };

  const reject = async (id) => {
    try {
      await api("/returns/" + id + "/reject", { method: "PUT", body: JSON.stringify({ notes: "Rejected" }) });
      toast("Return rejected", "success");
      load();
    } catch(e) { toast(e.message, "error"); }
  };

  const schedulePickup = async () => {
    try {
      await api("/returns/" + pickupModal + "/status", { method: "PUT", body: JSON.stringify({ status: "pickup_scheduled", ...pickupForm }) });
      toast("Pickup scheduled ✓", "success");
      setPickupModal(null);
      load();
    } catch(e) { toast(e.message, "error"); }
  };

  // stats
  const total     = returns.length;
  const pending   = returns.filter((r) => !["closed","rejected"].includes(r.status)).length;
  const received  = returns.filter((r) => ["received","inspected","closed"].includes(r.status)).length;
  const [aiAnalysis,   setAiAnalysis]   = useState(null);
  const [aiLoading,    setAiLoading]    = useState(false);
  const [showAiPanel,  setShowAiPanel]  = useState(false);
  const [tab,          setTab]          = useState("list");

  // ── Barcode Scanner State ────────────────────────────────────
  const [scanMode,     setScanMode]     = useState("camera"); // camera | manual
  const [scanning,     setScanning]     = useState(false);
  const [scanResult,   setScanResult]   = useState(null);
  const [manualSku,    setManualSku]    = useState("");
  const [scanError,    setScanError]    = useState("");
  const [scannedItems, setScannedItems] = useState([]);
  const [scanForm,     setScanForm]     = useState({ reason:"", condition:"good", notes:"", platform:"shopify", orderId:"" });
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanLoopRef = useRef(null);

  const refunded  = returns.filter((r) => r.resolution === "refund" && r.refundStatus === "processed").length;
  const totalRefundVal = returns.filter((r) => r.resolution === "refund").reduce((a, r) => a + r.refundAmount, 0);

  const filtered = returns.filter((r) => {
    const ms = filterStatus === "all" || r.status === filterStatus;
    const mp = filterPlat   === "all" || r.platform === filterPlat;
    return ms && mp;
  });

  const runAiAnalysis = async () => {
    if (returns.length === 0) { toast("No returns data to analyse", "error"); return; }
    setAiLoading(true); setShowAiPanel(true);
    try {
      // Prepare summary data for AI
      const summary = {
        totalReturns: returns.length,
        byReason: {},
        bySku: {},
        byPlatform: {},
        byCourier: {},
        byCustomer: {},
        refundTotal: returns.reduce((a, r) => a + (r.refundAmount || 0), 0),
        recentReturns: returns.slice(0, 50).map((r) => ({
          orderNumber:  r.orderNumber,
          sku:          r.productSku,
          productName:  r.productName,
          reason:       r.reason,
          platform:     r.platform,
          courier:      r.courierPartner,
          customerName: r.customerName,
          customerPhone:r.customerPhone,
          refundAmount: r.refundAmount,
          status:       r.status,
          date:         r.createdAt,
        })),
      };

      // Aggregate by reason
      returns.forEach((r) => {
        summary.byReason[r.reason]   = (summary.byReason[r.reason]   || 0) + 1;
        summary.bySku[r.productSku]  = { count:(summary.bySku[r.productSku]?.count||0)+1, name:r.productName };
        summary.byPlatform[r.platform] = (summary.byPlatform[r.platform] || 0) + 1;
        if (r.courierPartner) summary.byCourier[r.courierPartner] = (summary.byCourier[r.courierPartner] || 0) + 1;
        if (r.customerPhone)  summary.byCustomer[r.customerPhone]  = (summary.byCustomer[r.customerPhone] || 0) + 1;
      });

      const response = await fetch(API + "/ai/anthropic-proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + getToken() },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 3000,
          messages: [{
            role: "user",
            content: `You are a returns fraud and quality analyst for an Indian ecommerce leather belts brand (Vasudeva Enterprises / Leather Patta on Shopify, Amazon, Flipkart, Myntra, Meesho).

Analyse these return records and identify:
1. High-return SKUs (products with pattern problems)
2. Courier fraud signals (damaged in transit patterns by courier)
3. Fake return / serial returner detection (same customer/phone returning multiple times)
4. Return reason patterns and root causes
5. Actionable recommendations to reduce return rate by 20-30%

Return data summary:
${JSON.stringify(summary, null, 2)}

Return ONLY a JSON object with this exact structure (no markdown):
{
  "overview": {
    "returnRate": "string like '8.5%'",
    "topReason": "string",
    "riskLevel": "low|medium|high|critical",
    "estimatedFraudAmount": number,
    "summary": "2-3 sentence executive summary"
  },
  "highReturnSkus": [
    {
      "sku": "string",
      "productName": "string",
      "returnCount": number,
      "topReason": "string",
      "riskLevel": "low|medium|high",
      "recommendation": "string"
    }
  ],
  "courierFraud": [
    {
      "courier": "string",
      "damageCount": number,
      "fraudSignals": ["string"],
      "recommendation": "string",
      "riskLevel": "low|medium|high"
    }
  ],
  "serialReturners": [
    {
      "customerIdentifier": "string (masked phone/name)",
      "returnCount": number,
      "totalRefundClaimed": number,
      "pattern": "string",
      "action": "string"
    }
  ],
  "reasonAnalysis": [
    {
      "reason": "string",
      "count": number,
      "percentage": "string",
      "rootCause": "string",
      "fix": "string"
    }
  ],
  "recommendations": [
    {
      "priority": "immediate|short_term|long_term",
      "title": "string",
      "description": "string",
      "expectedImpact": "string like 'Reduce returns by 5%'"
    }
  ]
}`
          }]
        })
      });

      const data = await response.json();
      const text = data.content?.[0]?.text || "{}";
      const clean = text.replace(/\`\`\`json|\`\`\`/g, "").trim();
      const parsed = JSON.parse(clean);
      setAiAnalysis(parsed);
      toast("🤖 AI analysis complete", "success");
    } catch(e) { toast("Analysis failed: " + e.message, "error"); }
    setAiLoading(false);
  };

  return (
    <div>
      {/* stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 14 }}>
        {[
          { label: "Total Returns",  value: total,              color: "#2563eb" },
          { label: "Pending",        value: pending,            color: "#d97706" },
          { label: "Received",       value: received,           color: "#15803d" },
          { label: "Refund Value",   value: INR(totalRefundVal),color: "#dc2626" },
        ].map((k) => (
          <div key={k.label} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: "14px 16px" }}>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* filters + new button */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <select style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, fontFamily: "inherit", outline: "none" }}
          value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, fontFamily: "inherit", outline: "none" }}
          value={filterPlat} onChange={(e) => setFilterPlat(e.target.value)}>
          <option value="all">All Platforms</option>
          {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <span style={{ fontSize: 11, color: "#9ca3af" }}>{filtered.length} returns</span>
        <div style={{ display:"flex", gap:8, marginLeft:"auto" }}>
          <button style={{ ...S.btn("#6366f1") }} onClick={() => { setTab("analysis"); runAiAnalysis(); }}>
            🤖 AI Analysis
          </button>
          <button style={S.btn()} onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }}>
            + Log Return
          </button>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
        {[["scan","📷 Scan Return"],["list","📋 Returns List"],["analysis","🤖 AI Analysis"]].map(([id,label])=>(
          <button key={id} style={{ padding:"10px 20px", background:"none", border:"none", borderBottom:tab===id?"2px solid #F47920":"2px solid transparent", color:tab===id?"#F47920":"#6b7280", fontWeight:tab===id?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}
            onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* ══ AI ANALYSIS TAB ══ */}
      {tab === "analysis" && (
        <div>
          {aiLoading && (
            <div style={{ background:"#fff", borderRadius:10, border:"2px solid #6366f1", padding:60, textAlign:"center" }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🤖</div>
              <div style={{ fontWeight:700, fontSize:16, color:"#6366f1", marginBottom:8 }}>AI is analysing your returns…</div>
              <div style={{ fontSize:13, color:"#9ca3af" }}>Detecting fraud patterns, high-return SKUs and courier issues</div>
            </div>
          )}

          {!aiLoading && !aiAnalysis && (
            <div style={{ background:"#fff", borderRadius:10, border:"2px dashed #6366f1", padding:60, textAlign:"center" }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🔍</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>AI Return Analysis</div>
              <div style={{ fontSize:13, color:"#6b7280", maxWidth:440, margin:"0 auto 24px" }}>
                Detects high-return SKUs, courier fraud, serial returners and patterns in return reasons. Helps reduce return rate by 20-30%.
              </div>
              <button style={{ ...S.btn("#6366f1"), padding:"12px 28px", fontSize:14 }} onClick={runAiAnalysis} disabled={returns.length===0}>
                🤖 Run AI Analysis ({returns.length} returns)
              </button>
            </div>
          )}

          {!aiLoading && aiAnalysis && (() => {
            const riskColors = { low:["#15803d","#f0fdf4"], medium:["#d97706","#fffbeb"], high:["#dc2626","#fef2f2"], critical:["#7c2d12","#fef2f2"] };
            const priColors  = { immediate:["#dc2626","#fef2f2"], short_term:["#d97706","#fffbeb"], long_term:["#15803d","#f0fdf4"] };
            const [rc, rbg]  = riskColors[aiAnalysis.overview?.riskLevel] || riskColors.medium;

            return (
              <div>
                {/* Refresh button */}
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
                  <button style={{ ...S.btn("#6366f1"), fontSize:12 }} onClick={runAiAnalysis}>↻ Re-analyse</button>
                </div>

                {/* Overview card */}
                <div style={{ background:"linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius:10, padding:"18px 20px", marginBottom:14, color:"#fff" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>🤖 AI Analysis Overview</div>
                      <div style={{ fontSize:13, opacity:.9, lineHeight:1.5 }}>{aiAnalysis.overview?.summary}</div>
                    </div>
                    <div style={{ display:"flex", gap:12, flexShrink:0, flexWrap:"wrap" }}>
                      {[
                        ["Return Rate",      aiAnalysis.overview?.returnRate||"—"],
                        ["Top Reason",       aiAnalysis.overview?.topReason||"—"],
                        ["Est. Fraud Value", "₹"+(aiAnalysis.overview?.estimatedFraudAmount||0).toLocaleString("en-IN")],
                      ].map(([l,v])=>(
                        <div key={l} style={{ background:"rgba(255,255,255,0.15)", borderRadius:8, padding:"10px 14px", textAlign:"center" }}>
                          <div style={{ fontSize:9, opacity:.8, textTransform:"uppercase", marginBottom:3 }}>{l}</div>
                          <div style={{ fontWeight:700, fontSize:14 }}>{v}</div>
                        </div>
                      ))}
                      <div style={{ background:rbg, borderRadius:8, padding:"10px 14px", textAlign:"center" }}>
                        <div style={{ fontSize:9, color:rc, textTransform:"uppercase", marginBottom:3 }}>Risk Level</div>
                        <div style={{ fontWeight:700, fontSize:14, color:rc }}>{aiAnalysis.overview?.riskLevel?.toUpperCase()}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                  {/* High Return SKUs */}
                  <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                    <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, color:"#dc2626" }}>📦 High-Return SKUs</div>
                    <div style={{ padding:14 }}>
                      {(aiAnalysis.highReturnSkus||[]).length===0
                        ? <div style={{ textAlign:"center", padding:20, color:"#9ca3af", fontSize:12 }}>No high-return SKUs detected ✓</div>
                        : (aiAnalysis.highReturnSkus||[]).map((s,i)=>{
                            const [rc2,rbg2] = riskColors[s.riskLevel]||riskColors.medium;
                            return (
                              <div key={i} style={{ borderBottom:"1px solid #f3f4f6", paddingBottom:12, marginBottom:12 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                                  <div>
                                    <div style={{ fontWeight:600, fontSize:12 }}>{s.productName}</div>
                                    <div style={{ fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{s.sku}</div>
                                  </div>
                                  <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                                    <span style={{ background:"#fef2f2", color:"#dc2626", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>{s.returnCount} returns</span>
                                    <span style={{ background:rbg2, color:rc2, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>{s.riskLevel}</span>
                                  </div>
                                </div>
                                <div style={{ fontSize:11, color:"#6b7280", marginBottom:4 }}>Top reason: {s.topReason}</div>
                                <div style={{ background:"#fff7ed", borderRadius:5, padding:"5px 8px", fontSize:11, color:"#c2410c" }}>💡 {s.recommendation}</div>
                              </div>
                            );
                          })
                      }
                    </div>
                  </div>

                  {/* Courier Fraud */}
                  <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                    <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, color:"#d97706" }}>🚚 Courier Fraud Signals</div>
                    <div style={{ padding:14 }}>
                      {(aiAnalysis.courierFraud||[]).length===0
                        ? <div style={{ textAlign:"center", padding:20, color:"#9ca3af", fontSize:12 }}>No courier fraud signals detected ✓</div>
                        : (aiAnalysis.courierFraud||[]).map((c,i)=>{
                            const [rc3,rbg3] = riskColors[c.riskLevel]||riskColors.medium;
                            return (
                              <div key={i} style={{ borderBottom:"1px solid #f3f4f6", paddingBottom:12, marginBottom:12 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                                  <span style={{ fontWeight:700, fontSize:13 }}>🚚 {c.courier}</span>
                                  <span style={{ background:rbg3, color:rc3, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>{c.damageCount} damage cases</span>
                                </div>
                                <div style={{ marginBottom:6 }}>
                                  {(c.fraudSignals||[]).map((sig,j)=>(
                                    <div key={j} style={{ fontSize:11, color:"#6b7280", display:"flex", gap:5, marginBottom:2 }}><span style={{ color:"#d97706" }}>⚠</span>{sig}</div>
                                  ))}
                                </div>
                                <div style={{ background:"#fffbeb", borderRadius:5, padding:"5px 8px", fontSize:11, color:"#92400e" }}>💡 {c.recommendation}</div>
                              </div>
                            );
                          })
                      }
                    </div>
                  </div>
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                  {/* Serial Returners */}
                  <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                    <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, color:"#7c3aed" }}>👤 Serial Returners / Fake Returns</div>
                    <div style={{ padding:14 }}>
                      {(aiAnalysis.serialReturners||[]).length===0
                        ? <div style={{ textAlign:"center", padding:20, color:"#9ca3af", fontSize:12 }}>No suspicious patterns detected ✓</div>
                        : (aiAnalysis.serialReturners||[]).map((r,i)=>(
                            <div key={i} style={{ borderBottom:"1px solid #f3f4f6", paddingBottom:12, marginBottom:12 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                                <span style={{ fontWeight:600, fontSize:12 }}>{r.customerIdentifier}</span>
                                <span style={{ background:"#faf5ff", color:"#7c3aed", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>{r.returnCount}x returns</span>
                              </div>
                              <div style={{ fontSize:11, color:"#6b7280", marginBottom:4 }}>
                                Claimed: ₹{(r.totalRefundClaimed||0).toLocaleString("en-IN")} · {r.pattern}
                              </div>
                              <div style={{ background:"#faf5ff", borderRadius:5, padding:"5px 8px", fontSize:11, color:"#7c3aed" }}>🚨 {r.action}</div>
                            </div>
                          ))
                      }
                    </div>
                  </div>

                  {/* Reason Analysis */}
                  <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                    <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>📊 Return Reason Analysis</div>
                    <div style={{ padding:14 }}>
                      {(aiAnalysis.reasonAnalysis||[]).map((r,i)=>(
                        <div key={i} style={{ marginBottom:12, borderBottom:"1px solid #f3f4f6", paddingBottom:12 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                            <span style={{ fontWeight:600, fontSize:12 }}>{r.reason}</span>
                            <span style={{ fontSize:11, color:"#6b7280" }}>{r.count} ({r.percentage})</span>
                          </div>
                          <div style={{ background:"#f3f4f6", borderRadius:10, height:5, marginBottom:6 }}>
                            <div style={{ background:"#F47920", borderRadius:10, height:5, width:r.percentage }}/>
                          </div>
                          <div style={{ fontSize:10, color:"#6b7280", marginBottom:3 }}>Root cause: {r.rootCause}</div>
                          <div style={{ fontSize:10, color:"#15803d", fontWeight:600 }}>Fix: {r.fix}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                  <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, color:"#15803d" }}>✅ AI Recommendations to Reduce Return Rate</div>
                  <div style={{ padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                    {(aiAnalysis.recommendations||[]).map((r,i)=>{
                      const [pc,pbg] = priColors[r.priority]||priColors.short_term;
                      return (
                        <div key={i} style={{ display:"flex", gap:14, padding:"12px 14px", background:"#f9fafb", borderRadius:8, borderLeft:`4px solid ${pc}` }}>
                          <div style={{ flexShrink:0 }}>
                            <span style={{ background:pbg, color:pc, padding:"2px 10px", borderRadius:20, fontSize:10, fontWeight:700, whiteSpace:"nowrap" }}>
                              {r.priority==="immediate"?"🔴 Immediate":r.priority==="short_term"?"🟡 Short Term":"🟢 Long Term"}
                            </span>
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>{r.title}</div>
                            <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>{r.description}</div>
                            <div style={{ fontSize:11, color:"#15803d", fontWeight:600 }}>📈 {r.expectedImpact}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      
      {/* ══ SCAN RETURN TAB ══ */}
      {tab === "scan" && (
        <ScanReturn
          products={products}
          orders={orders}
          toast={toast}
          scannedItems={scannedItems}
          setScannedItems={setScannedItems}
          onLogReturn={async (item) => {
            try {
              await api("/returns", {
                method: "POST",
                body: JSON.stringify({
                  platform:    item.platform || "shopify",
                  orderId:     item.orderId  || "",
                  sku:         item.sku,
                  productName: item.name,
                  reason:      item.reason   || "Not specified",
                  condition:   item.condition|| "good",
                  quantity:    1,
                  refundAmount:item.price    || 0,
                  notes:       item.notes    || "",
                }),
              });
              toast("Return logged ✓", "success");
            } catch(e) { toast(e.message, "error"); }
          }}
        />
      )}

      {tab === "list" && (
      <div>
      {/* table */}
      <div style={S.card}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                {["Return #","Platform","Customer","Product","Reason","Status","Resolution","Restocked","Actions"].map((h) => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filtered.map((r) => {
                  const st   = STATUS_META[r.status] || STATUS_META.requested;
                  const plat = PLATFORMS.find((p) => p.id === r.platform) || PLATFORMS[4];
                  const next = STATUS_FLOW[STATUS_FLOW.indexOf(r.status) + 1];
                  const reason = REASONS.find((x) => x.id === r.reason);
                  return (
                    <tr key={r._id} onMouseEnter={(e) => e.currentTarget.style.background = "#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                      <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11, color: "#F47920" }}>{r.returnNumber}</td>
                      <td style={S.td}><Badge label={plat.name} color={plat.color} bg={plat.bg}/></td>
                      <td style={S.td}>
                        <div style={{ fontWeight: 500 }}>{r.customerName || "—"}</div>
                        {r.orderNumber && <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.orderNumber}</div>}
                      </td>
                      <td style={S.td}>
                        <div style={{ fontWeight: 500, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.productName}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>Qty: {r.quantity} · {INR(r.salePrice)}</div>
                      </td>
                      <td style={{ ...S.td, fontSize: 11, color: "#6b7280" }}>{reason?.label || r.reason}</td>
                      <td style={S.td}><Badge label={st.label} color={st.color} bg={st.bg}/></td>
                      <td style={S.td}>
                        {r.resolution === "pending" && <span style={{ fontSize: 11, color: "#9ca3af" }}>Pending</span>}
                        {r.resolution === "refund" && <Badge label={"Refund " + INR(r.refundAmount)} color="#dc2626" bg="#fef2f2"/>}
                        {r.resolution === "replacement" && <Badge label="Replacement" color="#7c3aed" bg="#f5f3ff"/>}
                      </td>
                      <td style={S.td}>
                        {r.restocked
                          ? <Badge label={"✓ " + r.restockedQuantity + " units"} color="#15803d" bg="#f0fdf4"/>
                          : <span style={{ fontSize: 11, color: "#9ca3af" }}>No</span>}
                      </td>
                      <td style={S.td}>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {/* approve */}
                          {r.status === "requested" && (
                            <button style={{ ...S.btn("#2563eb"), fontSize: 11, padding: "3px 8px" }} onClick={() => updateStatus(r._id, "approved")}>Approve</button>
                          )}
                          {/* schedule pickup */}
                          {r.status === "approved" && (
                            <button style={{ ...S.btn("#7c3aed"), fontSize: 11, padding: "3px 8px" }} onClick={() => { setPickupModal(r._id); setPickupForm({ awbNumber: "", courierName: "", pickupDate: "" }); }}>Schedule Pickup</button>
                          )}
                          {/* mark next status */}
                          {next && !["approved","closed","rejected"].includes(r.status) && r.status !== "requested" && (
                            <button style={{ ...S.btn("#F47920"), fontSize: 11, padding: "3px 8px" }} onClick={() => updateStatus(r._id, next)}>
                              → {STATUS_META[next]?.label}
                            </button>
                          )}
                          {/* restock */}
                          {["received","inspected"].includes(r.status) && !r.restocked && (
                            <button style={{ ...S.btn("#15803d"), fontSize: 11, padding: "3px 8px" }} onClick={() => restock(r._id)}>Restock</button>
                          )}
                          {/* resolve */}
                          {["received","inspected"].includes(r.status) && r.resolution === "pending" && (
                            <button style={{ ...S.btnOut, fontSize: 11, color: "#F47920", borderColor: "#fed7aa" }} onClick={() => { setResolveModal(r._id); setResolveForm({ resolution: "refund", refundAmount: r.salePrice }); }}>Resolve</button>
                          )}
                          {/* reject */}
                          {!["closed","rejected"].includes(r.status) && (
                            <button style={{ ...S.btnOut, color: "#dc2626", borderColor: "#fecaca" }} onClick={() => reject(r._id)}>Reject</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} style={{ ...S.td, textAlign: "center", color: "#9ca3af", padding: 40 }}>
                    No returns yet — click "+ Log Return" to add one
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* LOG RETURN MODAL */}
      {showModal && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div style={S.modal}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Log Return</span>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={S.label}>Platform</label>
                <select style={S.input} value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}>
                  {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Return Type</label>
                <select style={S.input} value={form.returnType} onChange={(e) => setForm((f) => ({ ...f, returnType: e.target.value }))}>
                  <option value="customer">Customer Initiated</option>
                  <option value="platform">Platform Initiated</option>
                </select>
              </div>
              <div>
                <label style={S.label}>Order Number</label>
                <input style={S.input} placeholder="e.g. #1234" value={form.orderNumber}
                  onChange={(e) => setForm((f) => ({ ...f, orderNumber: e.target.value }))}
                  list="order-list"/>
                <datalist id="order-list">
                  {orders.map((o, i) => <option key={i} value={o.name || o.id}/>)}
                </datalist>
              </div>
              <div>
                <label style={S.label}>Customer Name</label>
                <input style={S.input} placeholder="Customer name" value={form.customerName}
                  onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Customer Phone</label>
                <input style={S.input} placeholder="98XXXXXXXX" value={form.customerPhone}
                  onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Customer Email</label>
                <input style={S.input} type="email" placeholder="customer@email.com" value={form.customerEmail}
                  onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))}/>
              </div>
            </div>

            <div style={{ fontWeight: 600, fontSize: 13, margin: "6px 0 10px" }}>Product Details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={S.label}>Product Name *</label>
                <input style={S.input} placeholder="Product name" value={form.productName}
                  onChange={(e) => setForm((f) => ({ ...f, productName: e.target.value }))}
                  list="prod-list"/>
                <datalist id="prod-list">
                  {products.map((p) => <option key={p.id} value={p.name}/>)}
                </datalist>
              </div>
              <div>
                <label style={S.label}>SKU</label>
                <input style={S.input} placeholder="SKU" value={form.shopifySku}
                  onChange={(e) => setForm((f) => ({ ...f, shopifySku: e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Quantity</label>
                <input type="number" min="1" style={S.input} value={form.quantity}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}/>
              </div>
              <div>
                <label style={S.label}>Sale Price (₹)</label>
                <input type="number" min="0" style={S.input} value={form.salePrice}
                  onChange={(e) => setForm((f) => ({ ...f, salePrice: parseFloat(e.target.value) || 0 }))}/>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={S.label}>Return Reason</label>
                <select style={S.input} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}>
                  {REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Resolution</label>
                <select style={S.input} value={form.resolution} onChange={(e) => setForm((f) => ({ ...f, resolution: e.target.value }))}>
                  <option value="pending">Decide later</option>
                  <option value="refund">Refund</option>
                  <option value="replacement">Replacement</option>
                </select>
              </div>
              {form.resolution === "refund" && (
                <div>
                  <label style={S.label}>Refund Amount (₹)</label>
                  <input type="number" min="0" style={S.input} value={form.refundAmount}
                    onChange={(e) => setForm((f) => ({ ...f, refundAmount: parseFloat(e.target.value) || 0 }))}/>
                </div>
              )}
            </div>

            <label style={S.label}>Notes</label>
            <textarea style={{ ...S.input, height: 60, resize: "vertical" }} placeholder="Any additional notes"
              value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}/>

            <button style={{ ...S.btn(), width: "100%", padding: 11, fontSize: 14 }} onClick={createReturn}>
              Log Return
            </button>
          </div>
        </div>
      )}

      {/* SCHEDULE PICKUP MODAL */}
      {pickupModal && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setPickupModal(null)}>
          <div style={{ ...S.modal, width: 400 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Schedule Pickup</span>
              <button onClick={() => setPickupModal(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}>✕</button>
            </div>
            <label style={S.label}>Courier Name</label>
            <input style={S.input} placeholder="e.g. Delhivery, Shiprocket" value={pickupForm.courierName}
              onChange={(e) => setPickupForm((f) => ({ ...f, courierName: e.target.value }))}/>
            <label style={S.label}>AWB / Tracking Number</label>
            <input style={S.input} placeholder="AWB number" value={pickupForm.awbNumber}
              onChange={(e) => setPickupForm((f) => ({ ...f, awbNumber: e.target.value }))}/>
            <label style={S.label}>Pickup Date</label>
            <input type="date" style={S.input} value={pickupForm.pickupDate}
              onChange={(e) => setPickupForm((f) => ({ ...f, pickupDate: e.target.value }))}/>
            <button style={{ ...S.btn(), width: "100%", padding: 11, fontSize: 14 }} onClick={schedulePickup}>
              Confirm Pickup
            </button>
          </div>
        </div>
      )}

      {/* RESOLVE MODAL */}
      {resolveModal && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setResolveModal(null)}>
          <div style={{ ...S.modal, width: 400 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Resolve Return</span>
              <button onClick={() => setResolveModal(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}>✕</button>
            </div>
            <label style={S.label}>Resolution</label>
            <select style={S.input} value={resolveForm.resolution}
              onChange={(e) => setResolveForm((f) => ({ ...f, resolution: e.target.value }))}>
              <option value="refund">Refund customer</option>
              <option value="replacement">Send replacement</option>
            </select>
            {resolveForm.resolution === "refund" && (
              <>
                <label style={S.label}>Refund Amount (₹)</label>
                <input type="number" min="0" style={S.input} value={resolveForm.refundAmount}
                  onChange={(e) => setResolveForm((f) => ({ ...f, refundAmount: parseFloat(e.target.value) || 0 }))}/>
              </>
            )}
            <div style={{ background: resolveForm.resolution === "refund" ? "#fef2f2" : "#f5f3ff", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13, color: resolveForm.resolution === "refund" ? "#b91c1c" : "#7c3aed" }}>
              {resolveForm.resolution === "refund"
                ? "This will mark the refund as processed and close the return."
                : "This will mark a replacement as sent and close the return."}
            </div>
            <button style={{ ...S.btn(resolveForm.resolution === "refund" ? "#dc2626" : "#7c3aed"), width: "100%", padding: 11, fontSize: 14 }} onClick={resolve}>
              {resolveForm.resolution === "refund" ? "Process Refund" : "Send Replacement"}
            </button>
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  );
}


// ── ScanReturn Component ─────────────────────────────────────
function ScanReturn({ products, orders, toast, scannedItems, setScannedItems, onLogReturn }) {
  const [mode,       setMode]      = useState("manual"); // manual | camera
  const [manualSku,  setManualSku] = useState("");
  const [scanning,   setScanning]  = useState(false);
  const [camError,   setCamError]  = useState("");
  const [matched,    setMatched]   = useState(null);
  const [form,       setForm]      = useState({ reason:"", condition:"good", notes:"", platform:"shopify", orderId:"", qty:1 });
  const [logging,    setLogging]   = useState(false);
  const videoRef   = useRef(null);
  const streamRef  = useRef(null);
  const rafRef     = useRef(null);
  const canvasRef  = useRef(null);

  const REASONS    = ["Customer changed mind","Wrong product received","Size issue","Quality issue","Damaged in transit","Defective product","Not as described","Duplicate order","Other"];
  const CONDITIONS = [
    { id:"good",      label:"✅ Good — resellable as new" },
    { id:"opened",    label:"📦 Opened — resellable after check" },
    { id:"damaged",   label:"⚠️ Damaged — needs repair" },
    { id:"unusable",  label:"❌ Unusable — write off" },
  ];

  const S = {
    btn:    (c="#F47920") => ({ padding:"8px 16px", borderRadius:8, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
    btnOut: { padding:"7px 14px", borderRadius:8, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
    input:  { padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none", width:"100%" },
    label:  { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
    card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 },
    hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
  };

  // Lookup product by SKU or barcode
  const lookupSku = useCallback((sku) => {
    if (!sku?.trim()) return;
    const q   = sku.trim().toLowerCase();
    const hit = products.find(p =>
      (p.shopifySku||"").toLowerCase() === q ||
      (p.sku||"").toLowerCase() === q ||
      (p.asin||"").toLowerCase() === q ||
      (p.barcode||"").toLowerCase() === q ||
      p.name?.toLowerCase().includes(q)
    );
    if (hit) {
      setMatched(hit);
      toast("✅ Product found: " + hit.name, "success");
    } else {
      setMatched({ name: "Unknown Product", shopifySku: sku, price: 0, imageUrl: null, unknown: true });
      toast("⚠ SKU not in database — enter manually", "warn");
    }
  }, [products]);

  // Manual SKU submit
  const submitManual = () => {
    if (!manualSku.trim()) { toast("Enter SKU or product name", "error"); return; }
    lookupSku(manualSku);
  };

  // Camera barcode scanning via BarcodeDetector API
  const startCamera = async () => {
    setCamError("");
    setScanming(true); // typo guard — see below
    try {
      if (!("BarcodeDetector" in window)) {
        setCamError("Barcode scanning not supported on this browser. Use Chrome on Android or iOS Safari. Try Manual SKU instead.");
        setScanning(false);
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width:{ ideal:1280 }, height:{ ideal:720 } } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      const detector = new window.BarcodeDetector({ formats: ["ean_13","ean_8","code_128","qr_code","upc_a","upc_e","code_39","data_matrix"] });
      const scan = async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) { rafRef.current = requestAnimationFrame(scan); return; }
        try {
          const barcodes = await detector.detect(videoRef.current);
          if (barcodes.length > 0) {
            const code = barcodes[0].rawValue;
            stopCamera();
            setManualSku(code);
            lookupSku(code);
            return;
          }
        } catch {}
        rafRef.current = requestAnimationFrame(scan);
      };
      rafRef.current = requestAnimationFrame(scan);
    } catch(e) {
      setCamError("Camera access denied. Please allow camera permission and try again.");
      setScanning(false);
    }
  };

  // Fix the typo in startCamera
  const setScanming = setScanning;

  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setScanning(false);
  };

  useEffect(() => () => stopCamera(), []);

  const clearScan = () => { setMatched(null); setManualSku(""); setForm({ reason:"", condition:"good", notes:"", platform:"shopify", orderId:"", qty:1 }); };

  const logReturn = async () => {
    if (!matched) { toast("Scan or enter a product first", "error"); return; }
    if (!form.reason) { toast("Select a return reason", "error"); return; }
    setLogging(true);
    const item = {
      sku:       matched.shopifySku || matched.sku || manualSku,
      name:      matched.name,
      price:     matched.price || 0,
      imageUrl:  matched.imageUrl,
      reason:    form.reason,
      condition: form.condition,
      notes:     form.notes,
      platform:  form.platform,
      orderId:   form.orderId,
      qty:       form.qty,
      timestamp: new Date().toISOString(),
    };
    await onLogReturn(item);
    setScannedItems(prev => [item, ...prev]);
    clearScan();
    setLogging(false);
    toast("✅ Return logged successfully", "success");
  };

  return (
    <div>
      {/* Mode selector */}
      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
        <button style={{ ...S.btn(mode==="manual"?"#F47920":"#9ca3af"), flex:1, padding:12 }} onClick={()=>{ setMode("manual"); stopCamera(); }}>
          ⌨️ Enter SKU Manually
        </button>
        <button style={{ ...S.btn(mode==="camera"?"#2563eb":"#9ca3af"), flex:1, padding:12 }} onClick={()=>{ setMode("camera"); }}>
          📷 Scan Barcode
        </button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        {/* Left: Input */}
        <div>
          {/* Manual SKU entry */}
          {mode === "manual" && (
            <div style={S.card}>
              <div style={S.hdr}><span>⌨️ Enter SKU / Product Name</span></div>
              <div style={{ padding:16 }}>
                <div style={{ marginBottom:10 }}>
                  <label style={S.label}>SKU, ASIN, Barcode or Product Name</label>
                  <div style={{ display:"flex", gap:8 }}>
                    <input style={{ ...S.input, flex:1, fontSize:14 }}
                      placeholder="e.g. CB-BLK-001 or Black Formal Belt"
                      value={manualSku}
                      onChange={e=>setManualSku(e.target.value)}
                      onKeyDown={e=>{ if(e.key==="Enter") submitManual(); }}
                      autoFocus/>
                    <button style={{ ...S.btn(), padding:"8px 20px", fontSize:13 }} onClick={submitManual}>
                      🔍 Find
                    </button>
                  </div>
                  <div style={{ fontSize:11, color:"#9ca3af", marginTop:5 }}>
                    Works with Shopify SKU, Amazon ASIN, barcode, or partial product name
                  </div>
                </div>

                {/* Quick product picker */}
                {manualSku.length >= 2 && (
                  <div>
                    <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginBottom:6 }}>Matching products:</div>
                    <div style={{ maxHeight:180, overflowY:"auto", border:"1px solid #e5e7eb", borderRadius:8 }}>
                      {products.filter(p=>{
                        const q = manualSku.toLowerCase();
                        return (p.shopifySku||"").toLowerCase().includes(q) ||
                               (p.name||"").toLowerCase().includes(q) ||
                               (p.sku||"").toLowerCase().includes(q);
                      }).slice(0,8).map(p=>(
                        <div key={p.id||p.shopifyId} style={{ padding:"8px 12px", borderBottom:"1px solid #f3f4f6", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}
                          onClick={()=>{ setMatched(p); setManualSku(p.shopifySku||p.sku||""); toast("✅ "+p.name,"success"); }}
                          onMouseEnter={e=>e.currentTarget.style.background="#fff8f2"}
                          onMouseLeave={e=>e.currentTarget.style.background=""}>
                          {p.imageUrl
                            ? <img src={p.imageUrl} alt="" style={{ width:32,height:32,borderRadius:5,objectFit:"cover",flexShrink:0 }}/>
                            : <div style={{ width:32,height:32,borderRadius:5,background:"#f3f4f6",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0 }}>📦</div>}
                          <div>
                            <div style={{ fontSize:12,fontWeight:500 }}>{p.name}</div>
                            <div style={{ fontSize:10,color:"#9ca3af" }}>{p.shopifySku||p.sku} · ₹{p.price||0}</div>
                          </div>
                        </div>
                      ))}
                      {products.filter(p=>{
                        const q=manualSku.toLowerCase();
                        return (p.shopifySku||"").toLowerCase().includes(q)||(p.name||"").toLowerCase().includes(q);
                      }).length===0 && (
                        <div style={{ padding:12,fontSize:12,color:"#9ca3af",textAlign:"center" }}>
                          No products found — press Find to add as unknown
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Camera scanner */}
          {mode === "camera" && (
            <div style={S.card}>
              <div style={S.hdr}><span>📷 Camera Barcode Scanner</span></div>
              <div style={{ padding:16 }}>
                {camError ? (
                  <div style={{ background:"#fef2f2",borderRadius:8,padding:12,fontSize:12,color:"#dc2626",marginBottom:12 }}>
                    ⚠ {camError}
                    <div style={{ marginTop:8 }}>
                      <button style={{ ...S.btn("#6b7280"),fontSize:11 }} onClick={()=>{ setCamError(""); setMode("manual"); }}>
                        Switch to Manual Entry
                      </button>
                    </div>
                  </div>
                ) : !scanning ? (
                  <div style={{ textAlign:"center",padding:"20px 0" }}>
                    <div style={{ fontSize:48,marginBottom:12 }}>📷</div>
                    <div style={{ fontSize:13,color:"#6b7280",marginBottom:16 }}>
                      Point your camera at the product barcode
                    </div>
                    <button style={{ ...S.btn("#2563eb"),padding:"10px 24px",fontSize:13 }} onClick={startCamera}>
                      Start Camera
                    </button>
                    <div style={{ fontSize:11,color:"#9ca3af",marginTop:8 }}>Requires camera permission</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ position:"relative",background:"#000",borderRadius:8,overflow:"hidden",marginBottom:10 }}>
                      <video ref={videoRef} style={{ width:"100%",maxHeight:250,objectFit:"cover",display:"block" }} muted playsInline/>
                      {/* Scanning overlay */}
                      <div style={{ position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none" }}>
                        <div style={{ width:200,height:120,border:"3px solid #F47920",borderRadius:8,boxShadow:"0 0 0 2000px rgba(0,0,0,0.4)" }}/>
                      </div>
                      <div style={{ position:"absolute",bottom:8,left:0,right:0,textAlign:"center",color:"#fff",fontSize:11 }}>
                        🔍 Scanning for barcode…
                      </div>
                    </div>
                    <button style={{ ...S.btnOut,width:"100%",textAlign:"center" }} onClick={stopCamera}>
                      ✕ Stop Camera
                    </button>
                    <div style={{ fontSize:11,color:"#9ca3af",marginTop:8,textAlign:"center" }}>
                      Supports EAN-13, Code-128, QR Code, UPC
                    </div>
                  </div>
                )}
                {/* Manual fallback while in camera mode */}
                <div style={{ marginTop:12,paddingTop:12,borderTop:"1px solid #f3f4f6" }}>
                  <div style={{ fontSize:11,color:"#6b7280",marginBottom:6 }}>Or type barcode manually:</div>
                  <div style={{ display:"flex",gap:8 }}>
                    <input style={{ ...S.input,flex:1 }} placeholder="Type barcode…" value={manualSku}
                      onChange={e=>setManualSku(e.target.value)}
                      onKeyDown={e=>{ if(e.key==="Enter"){ stopCamera(); lookupSku(manualSku); }}}/>
                    <button style={S.btn()} onClick={()=>{ stopCamera(); lookupSku(manualSku); }}>Find</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Return details form — shows after product matched */}
          {matched && (
            <div style={S.card}>
              <div style={S.hdr}>
                <span>📋 Return Details</span>
                <button style={{ background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#9ca3af" }} onClick={clearScan}>×</button>
              </div>
              <div style={{ padding:16,display:"flex",flexDirection:"column",gap:10 }}>
                <div>
                  <label style={S.label}>Return Reason *</label>
                  <select style={S.input} value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))}>
                    <option value="">Select reason…</option>
                    {REASONS.map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Product Condition *</label>
                  <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
                    {CONDITIONS.map(con=>(
                      <label key={con.id} style={{ display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"6px 10px",borderRadius:7,border:"1px solid "+(form.condition===con.id?"#F47920":"#e5e7eb"),background:form.condition===con.id?"#fff8f2":"#fff",fontSize:12 }}>
                        <input type="radio" name="condition" value={con.id} checked={form.condition===con.id}
                          onChange={()=>setForm(f=>({...f,condition:con.id}))} style={{ accentColor:"#F47920" }}/>
                        {con.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
                  <div>
                    <label style={S.label}>Order ID</label>
                    <input style={S.input} placeholder="#1001" value={form.orderId} onChange={e=>setForm(f=>({...f,orderId:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>Platform</label>
                    <select style={S.input} value={form.platform} onChange={e=>setForm(f=>({...f,platform:e.target.value}))}>
                      {["shopify","amazon","flipkart","myntra","meesho","manual"].map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={S.label}>Notes (optional)</label>
                  <textarea style={{ ...S.input,height:60,resize:"vertical" }}
                    placeholder="Any additional notes about the return…"
                    value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
                </div>
                <button style={{ ...S.btn(),padding:12,fontSize:13,opacity:logging?0.7:1 }}
                  onClick={logReturn} disabled={logging||!form.reason}>
                  {logging?"Logging…":"✅ Log This Return"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Matched product + Session log */}
        <div>
          {/* Product card */}
          {matched ? (
            <div style={{ ...S.card,marginBottom:12 }}>
              <div style={S.hdr}><span>{matched.unknown?"⚠️ Unknown Product":"✅ Product Matched"}</span></div>
              <div style={{ padding:16,display:"flex",gap:14,alignItems:"flex-start" }}>
                {matched.imageUrl
                  ? <img src={matched.imageUrl} alt="" style={{ width:80,height:80,borderRadius:8,objectFit:"cover",flexShrink:0 }}/>
                  : <div style={{ width:80,height:80,borderRadius:8,background:"#f3f4f6",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,flexShrink:0 }}>📦</div>}
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700,fontSize:14,marginBottom:4 }}>{matched.name}</div>
                  <div style={{ fontSize:12,color:"#F47920",fontFamily:"monospace",marginBottom:6 }}>{matched.shopifySku||matched.sku||manualSku}</div>
                  <div style={{ display:"flex",gap:10,flexWrap:"wrap",fontSize:12 }}>
                    {matched.price>0 && <span style={{ color:"#15803d",fontWeight:600 }}>₹{matched.price}</span>}
                    {matched.category && <span style={{ background:"#f3f4f6",padding:"1px 8px",borderRadius:8 }}>{matched.category}</span>}
                    {matched.material && <span style={{ background:"#f3f4f6",padding:"1px 8px",borderRadius:8 }}>{matched.material}</span>}
                  </div>
                  {matched.unknown && (
                    <div style={{ marginTop:8,fontSize:11,color:"#d97706" }}>
                      ⚠ Product not in database. Return will be logged with entered SKU.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ ...S.card,padding:40,textAlign:"center",color:"#9ca3af",marginBottom:12 }}>
              <div style={{ fontSize:40,marginBottom:10 }}>📦</div>
              <div style={{ fontWeight:600,marginBottom:4 }}>No product scanned yet</div>
              <div style={{ fontSize:12 }}>Scan barcode or enter SKU to look up product</div>
            </div>
          )}

          {/* Session scan log */}
          <div style={S.card}>
            <div style={S.hdr}>
              <span>📋 Session Log ({scannedItems.length})</span>
              {scannedItems.length>0 && (
                <button style={{ background:"none",border:"none",fontSize:11,color:"#dc2626",cursor:"pointer" }}
                  onClick={()=>setScannedItems([])}>Clear</button>
              )}
            </div>
            {scannedItems.length===0 ? (
              <div style={{ padding:24,textAlign:"center",color:"#9ca3af",fontSize:12 }}>
                Returns logged this session will appear here
              </div>
            ) : (
              <div style={{ maxHeight:340,overflowY:"auto" }}>
                {scannedItems.map((item,i)=>{
                  const condColors={ good:"#15803d", opened:"#d97706", damaged:"#dc2626", unusable:"#9ca3af" };
                  return (
                    <div key={i} style={{ padding:"10px 16px",borderBottom:"1px solid #f3f4f6",display:"flex",gap:10,alignItems:"flex-start" }}>
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontWeight:500,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{item.name}</div>
                        <div style={{ fontSize:10,color:"#9ca3af",marginTop:2 }}>{item.sku} · {item.reason}</div>
                        <div style={{ fontSize:10,marginTop:2 }}>
                          <span style={{ color:condColors[item.condition]||"#6b7280",fontWeight:600 }}>
                            {item.condition==="good"?"✅ Good":item.condition==="opened"?"📦 Opened":item.condition==="damaged"?"⚠️ Damaged":"❌ Unusable"}
                          </span>
                        </div>
                      </div>
                      <div style={{ textAlign:"right",flexShrink:0 }}>
                        {item.price>0 && <div style={{ fontSize:12,fontWeight:600,color:"#dc2626" }}>-₹{item.price}</div>}
                        <div style={{ fontSize:9,color:"#9ca3af",marginTop:2 }}>{new Date(item.timestamp).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</div>
                      </div>
                    </div>
                  );
                })}
                {/* Session summary */}
                <div style={{ padding:"10px 16px",background:"#f9fafb",borderTop:"1px solid #e5e7eb" }}>
                  <div style={{ display:"flex",justifyContent:"space-between",fontSize:12 }}>
                    <span style={{ fontWeight:600 }}>Session total</span>
                    <span style={{ fontWeight:700,color:"#dc2626" }}>
                      ₹{scannedItems.reduce((a,i)=>a+(i.price||0),0).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div style={{ fontSize:11,color:"#9ca3af",marginTop:2 }}>
                    {scannedItems.filter(i=>i.condition==="good").length} resellable · {" "}
                    {scannedItems.filter(i=>i.condition==="damaged"||i.condition==="unusable").length} damaged/unusable
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
